import {
  addDays,
  addMonths,
  addWeeks,
  addYears,
  differenceInDays,
  differenceInMonths,
  differenceInWeeks,
  differenceInYears,
  format,
  isSameDay,
  isSameMonth,
  isSameWeek,
  isSameYear,
} from 'date-fns';

import { ApiSchemas } from '@/components/providers/v1-api';

type Customer = {
  past_events: ApiSchemas['ServiceEventRead'][];
  upcoming_events: ApiSchemas['ServiceEventRead'][];
};
type ServiceType = ApiSchemas['ServiceTypeRead'];
type ServiceEvent = ApiSchemas['ServiceEventRead'];

export function getLastCompletedEvent(customer: Customer, serviceType: ServiceType): ServiceEvent | undefined {
  return customer.past_events.find((e) => e.service_type_id === serviceType.id && e.status === 'COMPLETED');
}

export function getFirstCompletedEvent(customer: Customer, serviceType: ServiceType): ServiceEvent | undefined {
  const completedEvents = customer.past_events
    .filter((e) => e.service_type_id === serviceType.id && e.status === 'COMPLETED')
    .sort((a, b) => new Date(a.event.date).getTime() - new Date(b.event.date).getTime());

  return completedEvents[0];
}

export function getScheduledEvents(customer: Customer, serviceType: ServiceType): ServiceEvent[] {
  const pendingStatuses = ['PENDING', 'CONFIRMED'];
  const pastPending = customer.past_events.filter(
    (e) => e.service_type_id === serviceType.id && pendingStatuses.includes(e.status)
  );
  const upcomingPending = customer.upcoming_events.filter(
    (e) => e.service_type_id === serviceType.id && pendingStatuses.includes(e.status)
  );
  return [...pastPending, ...upcomingPending].sort((a, b) => {
    const today = new Date();
    const diffA = Math.abs(differenceInDays(new Date(a.event.date), today));
    const diffB = Math.abs(differenceInDays(new Date(b.event.date), today));
    return diffA - diffB;
  });
}

export function calculateNextServiceDate(customer: Customer, serviceType: ServiceType): Date | null {
  if (!serviceType.frequency_unit || serviceType.frequency_unit === 'MANUAL') return null;

  const firstEvent = getFirstCompletedEvent(customer, serviceType);
  const firstEventDate = firstEvent ? new Date(firstEvent?.event.date) : null;
  if (!firstEventDate) return null;

  // TODO: there's some more logic to this calculation. Some companies are ok with
  // customers drifting away from their original schedule. Need to add a setting for
  // this later on. For now, we stick with OG expected schedule.
  // Context: #SOL-733
  const startDate = firstEventDate;

  // We use `new Date()` here, because the goal is to show the CSR the current status
  // of the subscription. In this case we want to know if the next subscription service
  // is due, current, or in the future, according to today.
  const now = new Date();

  // Calculate how many periods have elapsed since the first service
  const periodsElapsed = calculatePeriodsSince(startDate, now, serviceType);

  // Check if we're currently in the expected period
  const expectedServiceDate = addPeriods(startDate, periodsElapsed, serviceType);
  if (isInSamePeriod(now, expectedServiceDate, serviceType)) return expectedServiceDate;

  // Otherwise, next service date is the next period
  const nextPeriod = periodsElapsed + 1;
  return addPeriods(startDate, nextPeriod, serviceType);
}

export const isReservice = (reserviceWindowDays: number, lastServiceEvent?: ServiceEvent): boolean => {
  if (!lastServiceEvent) return false;
  const lastServiceDate = new Date(lastServiceEvent.event.date);
  const now = new Date();
  const daysSinceLastService = differenceInDays(now, lastServiceDate);

  return daysSinceLastService <= reserviceWindowDays;
};

function calculatePeriodsSince(startDate: Date, currentDate: Date, serviceType: ServiceType): number {
  const { frequency_unit: unit, frequency_value: value } = serviceType;

  if (!unit || !value) return 0;

  switch (unit) {
    case 'DAY': {
      const totalDays = differenceInDays(currentDate, startDate);
      return Math.floor(totalDays / value);
    }
    case 'WEEK': {
      const totalWeeks = differenceInWeeks(currentDate, startDate);
      return Math.floor(totalWeeks / value);
    }
    case 'MONTH': {
      const totalMonths = differenceInMonths(currentDate, startDate);
      return Math.floor(totalMonths / value);
    }
    case 'YEAR': {
      const totalYears = differenceInYears(currentDate, startDate);
      return Math.floor(totalYears / value);
    }
    default:
      return 0;
  }
}

function addPeriods(startDate: Date, periods: number, serviceType: ServiceType): Date {
  const { frequency_unit: unit, frequency_value: value } = serviceType;

  if (!unit || !value) return new Date(startDate);

  const totalValue = periods * value;

  switch (unit) {
    case 'DAY':
      return addDays(startDate, totalValue);
    case 'WEEK':
      return addWeeks(startDate, totalValue);
    case 'MONTH':
      return addMonths(startDate, totalValue);
    case 'YEAR':
      return addYears(startDate, totalValue);
    default:
      return addMonths(startDate, totalValue);
  }
}

function isInSamePeriod(currentDate: Date, expectedDate: Date, serviceType: ServiceType): boolean {
  const { frequency_unit: unit } = serviceType;

  switch (unit) {
    case 'DAY':
      return isSameDay(currentDate, expectedDate);
    case 'WEEK':
      return isSameWeek(currentDate, expectedDate);
    case 'MONTH':
      return isSameMonth(currentDate, expectedDate);
    case 'YEAR':
      return isSameYear(currentDate, expectedDate);
    default:
      return false;
  }
}

export function formatDuePeriod(date: Date, serviceType: ServiceType): string {
  const now = new Date();

  switch (serviceType.frequency_unit) {
    case 'DAY':
      return `${format(date, 'MMMM d')}`;
    case 'WEEK':
      return `week of ${format(date, 'MMM d')}`;
    case 'MONTH':
      return `in ${format(date, 'MMMM')}`;
    case 'YEAR':
      return `in ${format(date, 'yyyy')}`;
    default:
      return `in ${format(date, 'MMMM')}`;
  }
}
